import { useSpaceAndPageIDFromRouter } from '@/data/space/hooks';
import Tooltip from '@/sharedComponents/TextEditor/components/Tooltip';
import { useCreatePageMutation } from '@/store';
import { checkRouteChildren } from '@/utils/checkRouteChildren';
import { CaretRight, Database, DotOutline, DotsThree, Plus } from '@phosphor-icons/react';
import { validUUID } from '@typedream/data';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { MouseEventHandler, useEffect, useState } from 'react';
import { MenuNav } from '../Nav';
import { PagesDnDWrapper } from './DnDWrapper';
import { DROP, PagesTreeItemProps } from './types';

/**
 * Replacing previous `Page` component.
 * @param props
 * @param props.path -
 */
export function PagesTreeItem({
  route,
  pages,
  path,
  pageID,
  pageIDPath,
  space,
  onOpenOptions,
  onCreate,
  openPage,
  pKey,
  isRoot = false,
}: PagesTreeItemProps) {
  const router = useRouter();
  const [spaceID] = useSpaceAndPageIDFromRouter();
  const { isLoading: loadingCreatePage } = useCreatePageMutation({
    fixedCacheKey: `createPage-${spaceID}`,
  });

  /** Flag to tell that page creation triggered from this component */
  const [createHere, setCreateHere] = useState(false);
  const [isPageOpen, setIsPageOpen] = useState(false);

  /** Flag for handling the drag-n-drop functionalities */
  const [dropDirection, setdropDirection] = useState(null);
  const [isOverCurrent, setisOverCurrent] = useState(null);

  const openPageToggle: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    setIsPageOpen((prevOpen) => !prevOpen);
  };

  const onCreatePage = () => {
    onCreate(route, path, setIsPageOpen);
    setCreateHere(true);
  };

  const renderSidebarItem = () => {
    if (route?.children && route?.children_keys?.length > 0) {
      return (
        <button
          type="button"
          className="flex items-center text-secondary bg-transparent hover:bg-neutral-300 rounded-full transition-all p-0.5 border-0"
          onClick={openPageToggle}
        >
          <CaretRight
            size={16}
            weight="bold"
            className={classNames('transition-all ease-out duration-200', {
              'rotate-90': isPageOpen,
            })}
          />
        </button>
      );
    }

    return <DotOutline size={16} weight="fill" />;
  };

  useEffect(() => {
    if (!loadingCreatePage) {
      setCreateHere(false);
    }
  }, [loadingCreatePage]);

  useEffect(() => {
    checkRouteChildren(route?.children || {}, ([, value]) => {
      if (value.page_id === pageID) setIsPageOpen(true);
    });
  }, [route]);

  return (
    <PagesDnDWrapper
      path={path}
      space={space}
      setdropDirection={setdropDirection}
      setisOverCurrent={setisOverCurrent}
      key={pKey}
      open={isPageOpen}
      setopen={setIsPageOpen}
      pageTreeRole={isRoot ? 'root-page' : 'child-page'}
    >
      <div
        className={classNames('relative', {
          'bg-background-highlight': dropDirection === DROP.INSIDE,
        })}
      >
        {isOverCurrent && (dropDirection === DROP.TOP || dropDirection === DROP.BOTTOM) && (
          <div
            className={classNames('absolute left-0 w-full h-0.5 bg-primary-400', {
              'top-0 -translate-x-[1px]': dropDirection === DROP.TOP,
              'top-full translate-x[1px]': dropDirection === DROP.BOTTOM,
            })}
          />
        )}
        <MenuNav
          pre={renderSidebarItem()}
          post={
            <div className="flex items-center gap-1">
              <Tooltip center="Page Options">
                <span
                  className="flex items-center bg-transparent p-0.5 rounded-full transition-all ease-out duration-200 hover:bg-neutral-300 cursor-pointer"
                  data-testid="SidePage_Page_openOptions_button"
                  role="none"
                  onClick={() => onOpenOptions(route?.page_id)}
                >
                  <DotsThree size={16} weight="bold" />
                </span>
              </Tooltip>

              {/* Hide add page button for template reference */}
              {!(
                route?.children_template_page_id && validUUID(route?.children_template_page_id)
              ) && (
                <>
                  {loadingCreatePage && createHere ? (
                    <div className="flex items-center justify-center">
                      <span
                        className={classNames('loader is-loading with-accent-border-color h-3 w-3')}
                      />
                    </div>
                  ) : (
                    <Tooltip center="Add Page Inside">
                      <span
                        className="flex items-center bg-transparent p-0.5 rounded-full transition-all ease-out duration-200 hover:bg-neutral-300 cursor-pointer"
                        role="none"
                        onClick={onCreatePage}
                      >
                        <Plus size={16} weight="bold" />
                      </span>
                    </Tooltip>
                  )}
                </>
              )}
            </div>
          }
          selected={pageID === route?.page_id}
          onClick={() =>
            openPage(
              pages[route?.page_id].metadata?.page_type === 'link_in_bio'
                ? `/link/s/${space?.id}/${route?.page_id}`
                : `/s/${space?.slug}/${space?.id}/${route?.page_id}`
            )
          }
          tooltip={pages[route?.page_id]?.name}
        >
          {pages[route?.page_id]?.name}
        </MenuNav>

        {
          // If current item has children, recursively render
          // this item's children.
          isPageOpen &&
            route?.children &&
            route?.children_keys?.map((childSlug) => {
              const childRoute = route?.children[childSlug];
              return (
                <PagesTreeItem
                  key={childSlug}
                  route={childRoute}
                  pages={pages}
                  path={[...path, childSlug]}
                  onCreate={onCreate}
                  space={space}
                  pageID={pageID}
                  pageIDPath={pageIDPath}
                  pKey={childRoute?.page_id}
                  onOpenOptions={onOpenOptions}
                  openPage={openPage}
                />
              );
            })
        }

        {
          // If the current item has template, render the template reference
          route?.children_template_page_id && validUUID(route?.children_template_page_id) && (
            <div className="ml-[24px]">
              <MenuNav
                pre={<Database size={16} weight="bold" className="text-accent" />}
                selected={
                  pageID === route?.children_template_page_id &&
                  !router?.pathname?.includes('template')
                }
                onClick={() =>
                  openPage(`/col/${space?.slug}/${space?.id}/${route?.children_template_page_id}`)
                }
              >{`${pages?.[route?.children_template_page_id]?.name} Items`}</MenuNav>
            </div>
          )
        }
      </div>
    </PagesDnDWrapper>
  );
}

PagesTreeItem.defaultProps = {
  isRoot: false,
};
